import { HicapsConnectMethod } from '@principle-theorem/hicaps-connect';
import {
  AnyFundingRequest,
  AppointmentStatus,
  BookingWindowType,
  CollectionGroup,
  IAIReceptionistKnowledgeBase,
  IAIReceptionistSettings,
  IAppointment,
  IAppointmentRequest,
  IBookingWindowTiming,
  IBrand,
  IBrandingSettings,
  IBridgeDevice,
  ICallEvent,
  IConsumableAsset,
  IContact,
  IDepositPaymentAttempt,
  IDepositRequest,
  IDocumentSettings,
  IEquipmentAsset,
  IHicapsConnectMerchant,
  IHicapsConnectTerminal,
  IHicapsConnectTerminalV2,
  IInstrumentAsset,
  IPatient,
  IPractice,
  IPracticeSettings,
  IPracticeSetupWizard,
  IProduct,
  IRecurringTaskConfiguration,
  IRosterTime,
  IScheduleSummary,
  ISchedulingAlert,
  ISchedulingEventSummary,
  ISmartpayTerminal,
  ISterilisationCycle,
  ISterilisationMachine,
  ISterilisationRecord,
  ITag,
  ITyroTerminal,
  IVerifoneTerminal,
  IWebsiteSnapshot,
  PracticeCollection,
  RequiredMohPracticeSetting,
  SYSTEM_DOCUMENT_DEFAULTS,
  UNPAID_DEPOSIT_STATUSES,
  isUnpaidDepositStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  CALENDAR_DAYS_OF_WEEK,
  CollectionReference,
  DayOfWeek,
  DocumentReference,
  Firestore,
  IReffable,
  ITimePeriod,
  ITimestampRange,
  TIME_FORMAT_24HR,
  Timezone,
  WithRef,
  addDoc,
  all$,
  chunkRange,
  collectionGroupQuery,
  initFirestoreModel,
  limit,
  mergeDayAndTime,
  multiFilter,
  multiSortBy$,
  multiUndeleted,
  nameSorter,
  orderBy,
  query$,
  slugify,
  subCollection,
  toMoment,
  toQuery,
  toTimePeriod,
  toTimestamp,
  undeletedFilter,
  undeletedQuery,
  where,
} from '@principle-theorem/shared';
import { compact, first, isNil, omitBy, sortBy } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Brand } from '../brand';
import { MohAgreementHolder } from '../funding-request/moh/moh-agreement-holder';
import { IHicapsConnectProcess } from '../integrations/hicaps-connect/hicaps-connect-api-adaptor';
import { Roster } from '../staffer/roster';
import { HicapsConnectMerchant } from './hicaps-connect-merchant';

export const DEFAULT_SCHEDULE_SUMMARY_PERIOD = 3;

/** Reaches into the past because a deposit missed on the day is still chaseable. */
export const APPOINTMENTS_AWAITING_DEPOSIT_WINDOW_DAYS = 7;

/** Caps a work queue, not a report — deliberately unpaginated. */
export const APPOINTMENTS_AWAITING_DEPOSIT_LIMIT = 50;

/**
 * Appointment statuses that put a deposit beyond recovery, so their rows are
 * noise in a chase list. This is the complete exclusion set — `AppointmentStatus`
 * has no no-show or missed member, a no-show is recorded as `Cancelled`.
 */
const nonRecoverableStatuses: AppointmentStatus[] = [
  AppointmentStatus.Cancelled,
  AppointmentStatus.Complete,
];

export class PracticeSettings {
  static init(): IPracticeSettings {
    return {
      timezone: Timezone.AustraliaSydney,
    };
  }

  static isAiReceptionistEnabled(practice: IPractice): boolean {
    return practice.settings?.aiReceptionist?.enabled === true;
  }

  static isAiReceptionistTestModeEnabled(
    practice: IPractice | undefined
  ): boolean {
    return practice?.settings?.aiReceptionist?.testModeEnabled === true;
  }
}

export class Practice {
  static init(overrides?: Partial<IPractice>): IPractice {
    const practice: IPractice = {
      name: '',
      address: '',
      phone: '',
      email: '',
      slug: '',
      // TODO: Implement in practice management
      yearOpened: 0,
      enabledForSchedulingAggregateTrial: true,
      settings: PracticeSettings.init(),
      schedule: Roster.init(),
      billingEntities: [],
      metadata: {},
      ...initFirestoreModel(),
      ...overrides,
    };
    return {
      ...practice,
      slug: slugify(practice.name.toLowerCase()),
    };
  }

  static brandRef(practice: IReffable<IPractice>): DocumentReference<IBrand> {
    return Firestore.getParentDocRef<IBrand>(practice.ref);
  }

  static callEventCol(
    practice: IReffable<IPractice>
  ): CollectionReference<ICallEvent> {
    return subCollection<ICallEvent>(
      practice.ref,
      PracticeCollection.CallEvents
    );
  }

  static aiReceptionistKnowledgeBaseCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IAIReceptionistKnowledgeBase> {
    return subCollection<IAIReceptionistKnowledgeBase>(
      practice.ref,
      PracticeCollection.AIReceptionistKnowledgeBase
    );
  }

  static aiReceptionistSettingsCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IAIReceptionistSettings> {
    return subCollection<IAIReceptionistSettings>(
      practice.ref,
      PracticeCollection.AIReceptionistSettings
    );
  }

  /**
   * Website Snapshots for the practice: the latest read of each Source, one
   * document per Source, reused by repeat Website Imports.
   */
  static aiReceptionistWebsiteSnapshotsCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IWebsiteSnapshot> {
    return subCollection<IWebsiteSnapshot>(
      practice.ref,
      PracticeCollection.AIReceptionistWebsiteSnapshots
    );
  }

  static mediaTagCol(
    practice: IReffable<IPractice>
  ): CollectionReference<ITag> {
    return subCollection<ITag>(practice.ref, PracticeCollection.MediaTags);
  }

  static depositRequestCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IDepositRequest> {
    return subCollection<IDepositRequest>(
      practice.ref,
      PracticeCollection.DepositRequests
    );
  }

  static depositPaymentAttemptCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IDepositPaymentAttempt> {
    return subCollection<IDepositPaymentAttempt>(
      practice.ref,
      PracticeCollection.DepositPaymentAttempts
    );
  }

  static mediaTags$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<ITag>[]> {
    return all$(undeletedQuery(Practice.mediaTagCol(practice)));
  }

  static instrumentCol(
    practice: WithRef<IPractice>
  ): CollectionReference<IInstrumentAsset> {
    return subCollection<IInstrumentAsset>(
      practice.ref,
      PracticeCollection.Instruments
    );
  }

  static instruments$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IInstrumentAsset>[]> {
    return all$(Practice.instrumentCol(practice));
  }

  static equipmentCol(
    practice: WithRef<IPractice>
  ): CollectionReference<IEquipmentAsset> {
    return subCollection<IEquipmentAsset>(
      practice.ref,
      PracticeCollection.Equipment
    );
  }

  static equipment$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IEquipmentAsset>[]> {
    return all$(Practice.equipmentCol(practice));
  }

  static fundingRequestCol<T extends AnyFundingRequest>(
    practice: IReffable<IPractice>
  ): CollectionReference<T> {
    return subCollection<T>(practice.ref, PracticeCollection.FundingRequests);
  }

  static consumableCol(
    practice: WithRef<IPractice>
  ): CollectionReference<IConsumableAsset> {
    return subCollection<IConsumableAsset>(
      practice.ref,
      PracticeCollection.Consumables
    );
  }

  static consumables$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IConsumableAsset>[]> {
    return all$(Practice.consumableCol(practice));
  }

  static scheduleSummaryCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IScheduleSummary> {
    return subCollection<IScheduleSummary>(
      practice.ref,
      PracticeCollection.ScheduleSummaries
    );
  }

  static defaultScheduleSummaryDateRange(
    practice: WithRef<IPractice>
  ): ITimePeriod {
    const timezone = practice.settings.timezone;
    return toTimePeriod(
      moment(),
      moment().add(DEFAULT_SCHEDULE_SUMMARY_PERIOD, 'months'),
      timezone
    );
  }

  static schedulingAlertCol(
    practice: WithRef<IPractice>
  ): CollectionReference<ISchedulingAlert> {
    return subCollection<ISchedulingAlert>(
      practice.ref,
      PracticeCollection.SchedulingAlerts
    );
  }

  static schedulingAlerts$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<ISchedulingAlert>[]> {
    return query$(undeletedQuery(Practice.schedulingAlertCol(practice)));
  }

  static activeSchedulingAlerts$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<ISchedulingAlert>[]> {
    return query$(
      Practice.schedulingAlertCol(practice),
      where('deleted', '==', false),
      where(
        'deadline',
        '<=',
        moment
          .tz(practice.settings.timezone)
          .endOf('day')
          .add(2, 'days')
          .toDate()
      )
    ).pipe(
      map((alerts) => {
        return alerts
          .filter((alert) => {
            return toMoment(alert.deadline).isSameOrAfter(
              moment.tz(practice.settings.timezone).startOf('day')
            );
          })
          .sort((alertA, alertB) => {
            if (!alertA.deadline || !alertB.deadline) {
              return 0;
            }
            return alertA.deadline.seconds > alertB.deadline.seconds ? 1 : -1;
          });
      })
    );
  }

  static recurringTaskConfigurationCol(
    practice: WithRef<IPractice>
  ): CollectionReference<IRecurringTaskConfiguration> {
    return subCollection<IRecurringTaskConfiguration>(
      practice.ref,
      PracticeCollection.RecurringTaskConfigurations
    );
  }

  static recurringTaskConfigurations$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IRecurringTaskConfiguration>[]> {
    return all$(Practice.recurringTaskConfigurationCol(practice));
  }

  static contactCol(
    practice: WithRef<IPractice>
  ): CollectionReference<IContact> {
    return subCollection<IContact>(practice.ref, PracticeCollection.Contacts);
  }

  static contacts$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IContact>[]> {
    return query$(undeletedQuery(Practice.contactCol(practice)));
  }

  static productCol(
    practice: WithRef<IPractice>
  ): CollectionReference<IProduct> {
    return subCollection<IProduct>(practice.ref, PracticeCollection.Products);
  }

  static products$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IProduct>[]> {
    return query$(undeletedQuery(Practice.productCol(practice)));
  }

  static sterilisationRecordCol(
    practice: IReffable<IPractice>
  ): CollectionReference<ISterilisationRecord> {
    return subCollection<ISterilisationRecord>(
      practice.ref,
      PracticeCollection.SterilisationRecords
    );
  }

  static sterilisationRecords$(
    practice: IReffable<IPractice>,
    range?: ITimestampRange
  ): Observable<WithRef<ISterilisationRecord>[]> {
    if (!range) {
      return query$(undeletedQuery(Practice.sterilisationRecordCol(practice)));
    }

    return query$(
      undeletedQuery(Practice.sterilisationRecordCol(practice)),
      where('createdAt', '<=', range.to),
      where('createdAt', '>=', range.from),
      orderBy('createdAt', 'desc')
    );
  }

  static sterilisationRecordsForPatient$(
    practice: IReffable<IPractice>,
    patientRef: IReffable<IPatient>
  ): Observable<WithRef<ISterilisationRecord>[]> {
    return query$(
      undeletedQuery(Practice.sterilisationRecordCol(practice)),
      where('patient.ref', '==', patientRef.ref),
      orderBy('createdAt', 'desc')
    );
  }

  static sterilisationCycleCol(
    practice: IReffable<IPractice>
  ): CollectionReference<ISterilisationCycle> {
    return subCollection<ISterilisationCycle>(
      practice.ref,
      PracticeCollection.SterilisationCycles
    );
  }

  static sterilisationCycles$(
    practice: IReffable<IPractice>,
    range?: ITimestampRange
  ): Observable<WithRef<ISterilisationCycle>[]> {
    if (!range) {
      return all$(Practice.sterilisationCycleCol(practice)).pipe(
        multiUndeleted()
      );
    }

    return Firestore.query$(
      toQuery(
        Practice.sterilisationCycleCol(practice),
        where('createdAt', '<=', range.to),
        where('createdAt', '>=', range.from),
        orderBy('createdAt', 'desc')
      )
    ).pipe(multiUndeleted());
  }

  static async getLatestCycleRecord(
    practice: IReffable<IPractice>,
    machineRef: DocumentReference<ISterilisationMachine>
  ): Promise<WithRef<ISterilisationCycle> | undefined> {
    const results = await Firestore.getDocs(
      toQuery(
        Practice.sterilisationCycleCol(practice),
        where('machine.ref', '==', machineRef),
        orderBy('createdAt', 'desc'),
        limit(1)
      )
    );
    return first(results);
  }

  static async addSterilisationRecord(
    practice: IReffable<IPractice>,
    record: ISterilisationRecord
  ): Promise<void> {
    await addDoc(Practice.sterilisationRecordCol(practice), record);
  }

  static openTime(practice: WithRef<IPractice>): moment.Moment {
    const earliestDay: IRosterTime = practice.schedule.days.reduce(
      (previous: IRosterTime, current: IRosterTime): IRosterTime => {
        return previous.shift.from < current.shift.from ? previous : current;
      }
    );

    return moment.tz(
      earliestDay.shift.from,
      TIME_FORMAT_24HR,
      practice.settings.timezone
    );
  }

  static dayOfWeekOpenTime(
    practice: WithRef<IPractice>,
    dayOfWeek: DayOfWeek
  ): moment.Moment | undefined {
    const foundDay = practice.schedule.days.find(
      (day) => day.dayOfWeek === dayOfWeek
    );
    if (!foundDay) {
      return;
    }
    return moment.tz(
      foundDay.shift.from,
      TIME_FORMAT_24HR,
      practice.settings.timezone
    );
  }

  static closeTime(practice: WithRef<IPractice>): moment.Moment {
    const latestDay: IRosterTime = practice.schedule.days.reduce(
      (previous: IRosterTime, current: IRosterTime): IRosterTime => {
        return previous.shift.to > current.shift.to ? previous : current;
      }
    );

    return moment.tz(
      latestDay.shift.to,
      TIME_FORMAT_24HR,
      practice.settings.timezone
    );
  }

  static dayOfWeekCloseTime(
    practice: WithRef<IPractice>,
    dayOfWeek: DayOfWeek
  ): moment.Moment | undefined {
    const foundDay = practice.schedule.days.find(
      (day) => day.dayOfWeek === dayOfWeek
    );
    if (!foundDay) {
      return;
    }

    return moment.tz(
      foundDay.shift.to,
      TIME_FORMAT_24HR,
      practice.settings.timezone
    );
  }

  static brandDoc(practice: IReffable<IPractice>): DocumentReference<IBrand> {
    return Firestore.getParentDocRef(practice.ref.path);
  }

  static unscheduledAppointments$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IAppointment>[]> {
    return all$(
      collectionGroupQuery<IAppointment>(
        CollectionGroup.Appointments,
        where('practice.ref', '==', practice.ref),
        where('status', '==', AppointmentStatus.Unscheduled)
      )
    );
  }

  static bridgeDeviceCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IBridgeDevice> {
    return subCollection<IBridgeDevice>(
      practice.ref,
      PracticeCollection.BridgeDevices
    );
  }

  static bridgeDevices$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<IBridgeDevice>[]> {
    return query$(
      Practice.bridgeDeviceCol(practice),
      where('deleted', '!=', true)
    ).pipe(map((device) => sortBy(device, ['name'])));
  }

  static tyroTerminalCol(
    practice: WithRef<IPractice>
  ): CollectionReference<ITyroTerminal> {
    return subCollection<ITyroTerminal>(
      practice.ref,
      PracticeCollection.TyroTerminals
    );
  }

  static tyroTerminals$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<ITyroTerminal>[]> {
    return all$(undeletedQuery(Practice.tyroTerminalCol(practice)));
  }

  static isTyroEnabled(practice?: IPractice): boolean {
    return practice?.tyroSettings?.isEnabled ?? false;
  }

  static smartpayTerminalCol(
    practice: WithRef<IPractice>
  ): CollectionReference<ISmartpayTerminal> {
    return subCollection<ISmartpayTerminal>(
      practice.ref,
      PracticeCollection.SmartpayTerminals
    );
  }

  static smartpayTerminals$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<ISmartpayTerminal>[]> {
    return all$(undeletedQuery(Practice.smartpayTerminalCol(practice)));
  }

  static isSmartpayEnabled(practice?: IPractice): boolean {
    return practice?.smartpaySettings?.isEnabled ?? false;
  }

  static verifoneTerminalCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IVerifoneTerminal> {
    return subCollection<IVerifoneTerminal>(
      practice.ref,
      PracticeCollection.VerifoneTerminals
    );
  }

  static verifoneTerminals$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<IVerifoneTerminal>[]> {
    return all$(Practice.verifoneTerminalCol(practice)).pipe(
      multiFilter(undeletedFilter),
      multiSortBy$(nameSorter())
    );
  }

  static isVerifoneEnabled(practice?: IPractice): boolean {
    return practice?.verifoneSettings?.isEnabled ?? false;
  }

  static isHicapsConnectEnabled(practice?: IPractice): boolean {
    return practice?.hicapsConnectSettings?.isEnabled ?? false;
  }

  static resolveDocumentSettings(
    practice?: IPractice
  ): Required<IDocumentSettings> {
    const settings = practice?.settings?.print?.documentSettings ?? {};
    return { ...SYSTEM_DOCUMENT_DEFAULTS, ...omitBy(settings, isNil) };
  }

  static hicapsConnectTerminalCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IHicapsConnectTerminal> {
    return subCollection<IHicapsConnectTerminal>(
      practice.ref,
      PracticeCollection.HicapsConnectTerminals
    );
  }

  static hicapsConnectTerminals$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<IHicapsConnectTerminal>[]> {
    return all$(
      undeletedQuery(Practice.hicapsConnectTerminalCol(practice))
    ).pipe(multiSortBy$(nameSorter()));
  }

  static hicapsConnectProcessCol<T extends HicapsConnectMethod>(
    practice: IReffable<IPractice>
  ): CollectionReference<IHicapsConnectProcess<T>> {
    return subCollection<IHicapsConnectProcess<T>>(
      practice.ref,
      PracticeCollection.HicapsConnectProcesses
    );
  }

  static useV2Terminals(practice?: IPractice): boolean {
    return practice?.hicapsConnectSettings?.useV2Terminals ?? false;
  }

  static hicapsConnectTerminalV2Col(
    practice: IReffable<IPractice>
  ): CollectionReference<IHicapsConnectTerminalV2> {
    return subCollection<IHicapsConnectTerminalV2>(
      practice.ref,
      PracticeCollection.HicapsConnectTerminalsV2
    );
  }

  static hicapsConnectTerminalsV2$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<IHicapsConnectTerminalV2>[]> {
    return all$(
      undeletedQuery(Practice.hicapsConnectTerminalV2Col(practice))
    ).pipe(multiSortBy$(nameSorter()));
  }

  static hicapsConnectMerchantCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IHicapsConnectMerchant> {
    return subCollection<IHicapsConnectMerchant>(
      practice.ref,
      PracticeCollection.HicapsConnectMerchants
    );
  }

  static hicapsConnectMerchants$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<IHicapsConnectMerchant>[]> {
    return all$(
      undeletedQuery(Practice.hicapsConnectMerchantCol(practice))
    ).pipe(
      multiSortBy$((merchant) =>
        HicapsConnectMerchant.displayName(merchant).toLowerCase()
      )
    );
  }

  static getAppointmentRequests$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<IAppointmentRequest>[]> {
    const brandRef = Practice.brandDoc(practice);
    return Brand.getAppointmentRequests$({ ref: brandRef }, practice.ref);
  }

  static openingHours(
    practice: WithRef<IPractice>,
    range: ITimePeriod
  ): ITimePeriod[] {
    return compact(
      chunkRange(range, 1, 'day').map((day) => {
        const fromTime = Practice.dayOfWeekOpenTime(
          practice,
          CALENDAR_DAYS_OF_WEEK[day.from.weekday()]
        );
        const toTime = Practice.dayOfWeekCloseTime(
          practice,
          CALENDAR_DAYS_OF_WEEK[day.from.weekday()]
        );
        if (!fromTime || !toTime) {
          return;
        }
        return {
          from: mergeDayAndTime(day.from, fromTime, practice.settings.timezone),
          to: mergeDayAndTime(day.from, toTime, practice.settings.timezone),
        };
      })
    );
  }

  static getAppointmentsWithUpcomingFollowUps$(
    practice: IReffable<IPractice>,
    queryLimit = 100
  ): Observable<WithRef<IAppointment>[]> {
    return all$(
      collectionGroupQuery<IAppointment>(
        CollectionGroup.Appointments,
        ...compact([
          where('practice.ref', '==', practice.ref),
          orderBy('activeFollowUp.followUpDate', 'asc'),
          where('activeFollowUp.followUpDate', '>', toTimestamp()),
          queryLimit ? limit(queryLimit) : undefined,
        ])
      )
    );
  }

  static getAppointmentsWithExistingFollowUps$(
    practice: IReffable<IPractice>,
    queryLimit = 100
  ): Observable<WithRef<IAppointment>[]> {
    return all$(
      collectionGroupQuery<IAppointment>(
        CollectionGroup.Appointments,
        ...compact([
          where('practice.ref', '==', practice.ref),
          orderBy('activeFollowUp.followUpDate', 'asc'),
          where('activeFollowUp.followUpDate', '<=', toTimestamp()),
          queryLimit ? limit(queryLimit) : undefined,
        ])
      )
    );
  }

  /**
   * Start of the deposit recovery window, exposed so the page and its specs
   * share one definition of "recent enough to chase".
   */
  static awaitingDepositWindowStart(): moment.Moment {
    return moment().subtract(APPOINTMENTS_AWAITING_DEPOSIT_WINDOW_DAYS, 'days');
  }

  /**
   * Appointments the practice is still owed money for. `windowStart` is a
   * parameter rather than an internal `moment()` so a caller re-checking the
   * results client-side compares against the very boundary the query used,
   * instead of a second `moment()` that has already drifted past it.
   * `queryLimit` lets small surfaces like sidebar badges avoid loading the full
   * work queue.
   */
  static getAppointmentsAwaitingDeposit$(
    practice: IReffable<IPractice>,
    windowStart: moment.Moment = Practice.awaitingDepositWindowStart(),
    queryLimit: number = APPOINTMENTS_AWAITING_DEPOSIT_LIMIT
  ): Observable<WithRef<IAppointment>[]> {
    return all$(
      collectionGroupQuery<IAppointment>(
        CollectionGroup.Appointments,
        where('practice.ref', '==', practice.ref),
        where('deposit.metadata.status', 'in', UNPAID_DEPOSIT_STATUSES),
        orderBy('event.from', 'asc'),
        where('event.from', '>=', toTimestamp(windowStart)),
        limit(queryLimit)
      )
    );
  }

  /**
   * Re-checks an awaiting-deposit appointment against the boundary the query
   * used. The Firestore cache serves optimistic snapshots, so a deposit settled
   * in another tab can otherwise linger in this list as something still to
   * chase.
   *
   * The status exclusion is deliberately client-side. Filtering `status` in the
   * query would require a new composite index — the existing one is exactly
   * `practice.ref` + `deposit.metadata.status` + `event.from` — so two
   * tradeoffs are accepted here instead:
   *
   * Tradeoff (i): the server-side `limit` in
   * `Practice.getAppointmentsAwaitingDeposit$` is applied BEFORE this
   * predicate runs, so cancelled and complete appointments still consume slots
   * in the result window and can push genuine outstanding deposits off the end
   * of the page. Accepted: this list is a capped work queue, not a report.
   *
   * Tradeoff (ii): a Complete appointment whose deposit was never paid becomes
   * invisible here, and no alternative surface lists it. Accepted: recovering
   * money for an appointment that has already happened is an invoicing
   * concern, not a scheduling one.
   */
  static isAwaitingDeposit(
    appointment: WithRef<IAppointment>,
    windowStart: moment.Moment
  ): boolean {
    if (nonRecoverableStatuses.includes(appointment.status)) {
      return false;
    }
    if (!isUnpaidDepositStatus(appointment.deposit?.metadata.status)) {
      return false;
    }
    return (
      !!appointment.event &&
      toMoment(appointment.event.from).isSameOrAfter(windowStart)
    );
  }

  static schedulingEventSummaryCol(
    practice: IReffable<IPractice>
  ): CollectionReference<ISchedulingEventSummary> {
    return subCollection<ISchedulingEventSummary>(
      practice.ref,
      PracticeCollection.SchedulingEventSummaries
    );
  }

  static setupWizardCol(
    practice: IReffable<IPractice>
  ): CollectionReference<IPracticeSetupWizard> {
    return subCollection<IPracticeSetupWizard>(
      practice.ref,
      PracticeCollection.SetupWizard
    );
  }

  static getBookingWindowTiming(
    practice: WithRef<IPractice>,
    type: BookingWindowType
  ): IBookingWindowTiming | undefined {
    const bookingWindow = practice.settings.patientPortal?.bookingWindow;
    return bookingWindow ? bookingWindow[type] : undefined;
  }

  static hasValidMohSettings(practice: WithRef<IPractice>): boolean {
    return Practice.missingMohSettings(practice).length === 0;
  }

  static missingMohSettings(
    practice: WithRef<IPractice>
  ): RequiredMohPracticeSetting[] {
    const mohSettings = practice.mohSettings;
    const alwaysRequired = [
      RequiredMohPracticeSetting.ClaimFormEmail,
      RequiredMohPracticeSetting.PreApprovalFormEmail,
    ];
    const requiredWhenClaimingUnderPractice = [
      RequiredMohPracticeSetting.PayeeNumber,
      RequiredMohPracticeSetting.AgreementNumber,
    ];
    const required = MohAgreementHolder.claimsUnderPractice(practice)
      ? [...alwaysRequired, ...requiredWhenClaimingUnderPractice]
      : alwaysRequired;

    return required.filter((setting) => !mohSettings?.[setting]);
  }

  static logoStoragePath(practice: IReffable<IPractice>): string {
    return `${practice.ref.path}/logo`;
  }

  static accStoragePath(practice: IReffable<IPractice>): string {
    return `${practice.ref.path}/acc`;
  }

  static updateBrandingSettings(
    practice: WithRef<IPractice>,
    updates: Partial<IBrandingSettings>
  ): IBrandingSettings {
    return {
      ...(practice.branding ?? {}),
      ...updates,
    };
  }
}
